/**
 * KIR-4269 — the Evidence state model for Alert Details.
 *
 * Evidence for a finding is always in exactly one of four states, and the screen
 * must make clear which applies without implying the finding didn't happen:
 *
 * - `available`      — the specific flagged item is shown (an excerpt, a command,
 *                      a config value, a matched list entry, a package@version,
 *                      the matched code / decoded payload / surrounding text).
 * - `redacted`       — evidence is shown but a secret / personal fragment is
 *                      masked (`AlertSourceField.masked`, the PII redacted
 *                      sample, secret details). Still "available", flagged masked.
 * - `unavailable`    — the finding type *should* carry evidence but this row does
 *                      not (legacy / gated / not-recorded, or a DLP verdict-only
 *                      row per KIR-4593). Surfaced honestly by `DetectionLogic`.
 * - `not_applicable` — the finding type has no detected content to show at all,
 *                      because the alert reports a scan **coverage gap, not a
 *                      finding**. Distinct from `unavailable`: nothing was
 *                      detected, so there is no evidence that is merely missing.
 *
 * The first three are derived from which fields are present on the alert and are
 * already rendered by the existing evidence surfaces (`AlertSourceDetails`,
 * `AlertSecretDetails`, the excerpt panels, `DetectionLogic`). This module owns
 * the one state none of them modelled: `not_applicable`. Deriving it FE-side —
 * from the finding `type` alone — matches the rest of the evidence model, which
 * carries no state enum on the wire (see `contracts/alerts.ts`).
 */
export type EvidenceState =
  | 'available'
  | 'redacted'
  | 'unavailable'
  | 'not_applicable';

/**
 * Finding types whose evidence is genuinely `not_applicable`: the alert reports
 * that a scan could not complete, so no content was ever detected.
 *
 * - `content-size-exceeded` — an AI-instruction file was too large to scan; no
 *   content analysis ran. Its `TYPE_DISPLAY` copy names it a "Coverage gap, not
 *   a finding", and it carries no per-finding evidence field — only the
 *   type-level "too large" description.
 *
 * Deliberately narrow, and it excludes the other "coverage gap" type,
 * `api_error`: an MCP dependency scan that errors still records a finding-
 * specific `reason` (`formatError(error)`) plus the failing `package`/`version`/
 * `ecosystem`, which the Detection Logic section renders as an authored reason.
 * That is `available` evidence, so marking `api_error` not-applicable would hide
 * real detail — the opposite of the honest signalling this state is for.
 *
 * A type that merely lacks evidence on some rows (DLP verdict-only rows, legacy
 * findings, gated fields) is `unavailable`, not `not_applicable` — that
 * distinction is the point of the state (KIR-4269), and conflating the two would
 * imply "nothing happened" on real findings.
 */
export const EVIDENCE_NOT_APPLICABLE_TYPES: ReadonlySet<string> = new Set([
  'content-size-exceeded',
]);

/**
 * User-facing copy for the `not_applicable` evidence state. Names the reason
 * (a coverage gap) so it reads distinctly from the `unavailable` copy, and never
 * implies the underlying issue didn't occur.
 */
export const EVIDENCE_NOT_APPLICABLE_COPY =
  'This alert reports a scan coverage gap, not detected content, so there is no evidence to show.';

/**
 * Whether the given finding `type` has no detected content to show as evidence
 * (a scan coverage gap). Nullish / unrecognised types are treated as
 * evidence-bearing, so a new type falls through to the honest available /
 * unavailable path rather than being silently hidden as not-applicable.
 */
export function isEvidenceNotApplicable(
  type: string | null | undefined
): boolean {
  return type != null && EVIDENCE_NOT_APPLICABLE_TYPES.has(type);
}
